// Registry of known sign-in destinations. This is the ONLY file to edit when
// registering a new service, internal tool, or legacy host, or when changing
// how a destination is labelled.

// ─── Registration ───────────────────────────────────────────────────────────

// Display names for internal tools hosted under *.internal.sellersflow.com,
// keyed by the tool's subdomain slug. Every *.internal label is prefixed with
// "internal". Tools missing here get an auto-derived name: delimited slugs are
// title-cased, undelimited ones are segmented against SEGMENT_WORDS, and
// anything unrecognizable becomes "Internal Resource (<slug>)".
export const INTERNAL_TOOL_LABELS: Record<string, string> = {
  pricetracker: 'Price Tracker',
  payperclick: 'Amazon PPC Expert',
  cx: 'CX MVP Tool',
  extrator: 'Extrator de Conteúdo',
};

// Legacy hosts we still recognize, exact host → entry. Flagged so the UI can
// show a migration warning. When `replacedBy` is set, the auth handoff is
// rewritten to that host (https) and the user lands on the new address.
// SECURITY: keep this a closed list of exact hosts. *.web.app and
// *.firebaseapp.com are shared Firebase domains where anyone can claim a
// project name — never match them by pattern.
export interface LegacyHostEntry {
  label: string;
  replacedBy?: string;
}

export const LEGACY_HOSTS: Record<string, LegacyHostEntry> = {
  'price-tracker-sellersflow.web.app': {
    label: 'Price Tracker',
    replacedBy: 'pricetracker.internal.sellersflow.com',
  },
  'payperclick-expert.web.app': {
    label: 'Amazon PPC Expert',
    replacedBy: 'payperclick.internal.sellersflow.com',
  }
};

// Known services; first match wins, so keep specific rules above the generic
// *.sellersflow.com catch-all.
export const KNOWN_SERVICES: Array<{
  match: (host: string) => boolean;
  label: string | ((host: string) => string);
}> = [
  { match: (h) => h.endsWith('.chromiumapp.org'), label: 'the SellersFlow Extension' },
  { match: (h) => h === 'dataflow.sellersflow.com' || h.endsWith('.dataflow.sellersflow.com'), label: 'DataFlow' },
  { match: (h) => h.endsWith('.internal.sellersflow.com'), label: internalToolLabel },
  // Client-facing tool, so it lives outside *.internal — named explicitly
  // because the *.sellersflow.com catch-all below would just say
  // 'SellersFlow' on its sign-in screen.
  { match: (h) => h === 'calculadora.sellersflow.com', label: 'Calculadora de Rentabilidade' },
  { match: (h) => h in LEGACY_HOSTS, label: (h) => `legacy ${LEGACY_HOSTS[h]?.label ?? 'service'}` },
  { match: (h) => h.endsWith('.sellersflow.com'), label: 'SellersFlow' },
];

// ─── Lookup API (used by handoff.ts) ────────────────────────────────────────

export function labelForHost(host: string): string {
  const label = KNOWN_SERVICES.find((s) => s.match(host))?.label;
  if (typeof label === 'function') return label(host);
  return label ?? host;
}

export function isLegacyHost(host: string): boolean {
  return host in LEGACY_HOSTS;
}

export function legacyReplacementHost(host: string): string | null {
  return LEGACY_HOSTS[host]?.replacedBy ?? null;
}

// Trusted extension IDs: VITE_EXTENSION_ID (comma-separated, store build +
// optionally a pinned dev-build ID) plus VITE_EXTENSION_DEV_ID (single unpacked
// dev-build ID), merged so both work together. First entry is used as the
// fallback redirect base.
const EXTENSION_IDS = [
  ...((import.meta.env.VITE_EXTENSION_ID as string) ?? '').split(','),
  (import.meta.env.VITE_EXTENSION_DEV_ID as string) ?? '',
]
  .map((id) => id.trim())
  .filter(Boolean);

export const PRIMARY_EXTENSION_ID = EXTENSION_IDS[0] ?? '';

// ESCAPE HATCH — set VITE_ALLOW_ANY_EXTENSION=true to trust EVERY Chrome
// extension redirect (*.chromiumapp.org). chromiumapp.org is shared across
// all extensions, so with this on, any extension a teammate installs (or an
// attacker's, via a crafted sign-in link) can receive auth tokens. Intended
// ONLY for dev/homolog while unpacked-build extension IDs are unstable.
// NEVER enable on production.
const ALLOW_ANY_EXTENSION = (import.meta.env.VITE_ALLOW_ANY_EXTENSION as string) === 'true';

// ESCAPE HATCH — comma-separated localhost ports allowed to receive an auth
// handoff (e.g. "3000,5173"), so a client running at http://localhost:<port>
// can get tokens while being debugged. When set it governs localhost in EVERY
// build; when empty, localhost is only allowed under `vite dev`. Baked in at
// build time like all VITE_ vars — NEVER set for a production deploy, or a
// crafted sign-in link could hand tokens to whatever listens on that port on
// the victim's own machine.
const DEV_LOCALHOST_PORTS = ((import.meta.env.VITE_DEV_LOCALHOST_PORTS as string) ?? '')
  .split(',')
  .map((p) => p.trim())
  .filter(Boolean);

// Hosts allowed to receive an auth handoff. Anything not matched here is
// blocked before sign-in is offered, and buildRedirectUrl refuses to send
// tokens to it. chromiumapp.org is shared across ALL Chrome extensions, so
// only our exact extension IDs are trusted — never the whole domain (unless
// the dev-only escape hatch above is on).
export function isAllowedHost(host: string, port = ''): boolean {
  if (host === 'sellersflow.com' || host.endsWith('.sellersflow.com')) return true;
  if (host in LEGACY_HOSTS) return true;
  if (host.endsWith('.chromiumapp.org')) {
    if (ALLOW_ANY_EXTENSION) return true;
    const id = host.slice(0, -'.chromiumapp.org'.length);
    return EXTENSION_IDS.includes(id);
  }
  if (host === 'localhost' || host === '127.0.0.1') {
    if (DEV_LOCALHOST_PORTS.length > 0) return DEV_LOCALHOST_PORTS.includes(port);
    return import.meta.env.DEV;
  }
  return false;
}

// ─── Legacy handoff sunset (extension-only grace period) ────────────────────
//
// SSO v2 (code+PKCE via SF-DataFlow-API — see handoff.ts's buildCodeRedirectUrl)
// replaces the #payload= fragment handoff below for every migrated client.
// The BrowserKit extension is the one consumer that can't move the instant
// this ships: a Chrome Web Store update needs Google's review (hours to
// ~2 weeks for an identity-permission extension touching its OAuth flow),
// plus the browser's own background rollout after approval. Every other
// consumer (DataFlow, Inside-Sales, PayPerClick, price-tracker) is expected
// to send client_id as part of this same migration — there is no
// general-purpose legacy fallback left, only this bounded, dated exception
// for the one client that genuinely cannot update on demand.
//
// VITE_LEGACY_SSO_SUNSET_AT: ISO datetime (e.g. "2026-08-28T00:00:00Z"),
// baked in at build time like every VITE_ var (see SF-DataFlow's CLAUDE.md
// Maintenance Mode section for the identical pattern) — flipping it needs a
// redeploy. Unset = no cutoff armed yet (legacy stays open for the
// extension only, matching Maintenance Mode's own "unset END_AT = no
// countdown" default) — set it deliberately when arming the 14-day clock.
const LEGACY_SSO_SUNSET_AT = (import.meta.env.VITE_LEGACY_SSO_SUNSET_AT as string) || '';

export function isChromiumappHost(host: string): boolean {
  return host.endsWith('.chromiumapp.org');
}

export function legacySunsetDate(): Date | null {
  if (!LEGACY_SSO_SUNSET_AT) return null;
  const d = new Date(LEGACY_SSO_SUNSET_AT);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isLegacySunset(): boolean {
  const sunset = legacySunsetDate();
  return sunset !== null && Date.now() >= sunset.getTime();
}

// Whether the #payload= fragment handoff may still be used for this
// destination. Extension-only, unconditionally (the restriction that ships
// with this change, independent of whether a sunset date is configured) —
// and only before the sunset once one is armed.
export function isLegacyHandoffEligible(host: string): boolean {
  if (!isChromiumappHost(host)) return false;
  return !isLegacySunset();
}

// ─── Label derivation machinery ─────────────────────────────────────────────

// Vocabulary used to split undelimited slugs (e.g. "pricetracker") into
// words. Extend when a new tool's slug doesn't segment.
const SEGMENT_WORDS = new Set([
  'account', 'accounts', 'admin', 'ads', 'agent', 'ai', 'alert', 'alerts', 'analytics',
  'api', 'app', 'asin', 'audit', 'auth', 'auto', 'beta', 'billing', 'board', 'bot',
  'brand', 'browser', 'builder', 'catalog', 'center', 'chart', 'charts', 'check',
  'checker', 'client', 'cloud', 'console', 'control', 'dash', 'dashboard', 'data',
  'db', 'deal', 'deals', 'demo', 'desk', 'dev', 'docs', 'edit', 'editor', 'email',
  'engine', 'event', 'events', 'explorer', 'export', 'feed', 'feeds', 'file', 'files',
  'finance', 'find', 'finder', 'flow', 'forecast', 'form', 'forms', 'gen', 'generator',
  'health', 'help', 'here', 'hub', 'import', 'inbox', 'insight', 'insights',
  'inventory', 'invoice', 'invoices', 'job', 'jobs', 'key', 'keys', 'keyword',
  'keywords', 'kit', 'lab', 'labs', 'ledger', 'link', 'links', 'list', 'listing',
  'listings', 'live', 'log', 'logs', 'mail', 'manage', 'manager', 'map', 'margin',
  'market', 'metric', 'metrics', 'model', 'monitor', 'notes', 'order', 'orders',
  'page', 'pages', 'panel', 'pay', 'payments', 'payout', 'payouts', 'plan', 'planner',
  'portal', 'ppc', 'price', 'prices', 'pricing', 'product', 'products', 'profit',
  'proxy', 'queue', 'rank', 'ranking', 'refund', 'refunds', 'repo', 'report',
  'reports', 'repricer', 'research', 'return', 'returns', 'revenue', 'review',
  'reviews', 'sale', 'sales', 'scan', 'scanner', 'scraper', 'search', 'seller',
  'sellers', 'service', 'settings', 'setup', 'ship', 'shipping', 'shop', 'site',
  'something', 'stats', 'status', 'stock', 'store', 'studio', 'suite', 'support',
  'sync', 'system', 'tag', 'tags', 'task', 'tasks', 'team', 'test', 'ticket',
  'tickets', 'time', 'tool', 'tools', 'trace', 'track', 'tracker', 'tracking',
  'user', 'users', 'view', 'viewer', 'watch', 'watcher', 'web', 'wiki', 'workflow',
]);

const MAX_SEGMENT_WORD = 10;

// Word-break DP: split an undelimited slug into the fewest SEGMENT_WORDS
// covering it entirely, or null when no full segmentation exists.
function segmentSlug(slug: string): string[] | null {
  const best: Array<string[] | null> = new Array(slug.length + 1).fill(null);
  best[0] = [];
  for (let i = 1; i <= slug.length; i++) {
    for (let j = Math.max(0, i - MAX_SEGMENT_WORD); j < i; j++) {
      const prev = best[j];
      if (!prev) continue;
      const word = slug.slice(j, i);
      if (!SEGMENT_WORDS.has(word)) continue;
      const current = best[i];
      if (!current || prev.length + 1 < current.length) best[i] = [...prev, word];
    }
  }
  return best[slug.length];
}

function titleCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function internalToolLabel(host: string): string {
  const slug = host.slice(0, -'.internal.sellersflow.com'.length);
  const mapped = INTERNAL_TOOL_LABELS[slug];
  if (mapped) return `internal ${mapped}`;

  const words = /[-.]/.test(slug) ? slug.split(/[-.]+/).filter(Boolean) : segmentSlug(slug);
  if (words && words.length > 0) return `internal ${words.map(titleCase).join(' ')}`;
  return `Internal Resource (${slug})`;
}
